import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { useTheme, THEME_LABELS } from './ThemeContext';
import { User, Lock } from 'lucide-react';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { 
    isDarkMode, 
    toggleTheme, 
    currentTheme, 
    changeTheme, 
    themeLabels 
  } = useTheme();

  // Яркая светлая тема в стиле shadcn
  const getBackgroundClass = () => {
    if (currentTheme === 'dark') return 'enhanced-dark-bg';
    if (currentTheme === 'grither') return 'grither-gradient-bg grither-bg-pattern';
    if (currentTheme === 'custom') return 'universal-app-background';
    return 'bg-gradient-to-br from-sky-50 via-white to-indigo-50';
  };

  const getCardClass = () => {
    if (currentTheme === 'dark') return 'enhanced-dark-card shadow-2xl shadow-gray-900/50';
    if (currentTheme === 'grither') return 'bg-white/95 backdrop-blur-md grither-shadow-xl border border-grither-gray-200';
    if (currentTheme === 'custom') return 'universal-card shadow-2xl';
    return 'bg-white shadow-xl shadow-slate-200/60 border border-slate-100';
  };

  const getTextClass = (variant = 'primary') => {
    if (currentTheme === 'dark') {
      return variant === 'primary' ? 'text-white' : 'text-gray-300';
    }
    if (currentTheme === 'grither') {
      return variant === 'primary' ? 'grither-text-primary' : 
             variant === 'secondary' ? 'grither-text-secondary' : 'grither-text-tertiary';
    }
    if (currentTheme === 'custom') {
      return variant === 'primary' ? 'universal-text-primary' : 
             variant === 'secondary' ? 'universal-text-secondary' : 'universal-text-tertiary';
    }
    return variant === 'primary' ? 'text-slate-900' : 'text-slate-600';
  };

  const getButtonClass = () => {
    if (currentTheme === 'dark') return 'enhanced-dark-btn text-white';
    if (currentTheme === 'grither') return 'grither-btn-primary';
    if (currentTheme === 'custom') return 'universal-btn-primary text-white';
    return 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/25';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);
    
    if (!result.success) {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className={`min-h-screen transition-all duration-300 ${getBackgroundClass()} flex items-center justify-center p-4`}>
      
      <div className={`w-full max-w-md transform transition-all duration-500 hover:scale-102 ${getCardClass()} rounded-3xl p-8`}>
        
        <div className="text-center mb-8">
          {/* Enhanced Logo */}
          <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center transform transition-all duration-500 hover:rotate-12 hover:scale-110 ${
            currentTheme === 'dark'
              ? 'bg-gradient-to-br from-blue-600 to-purple-600 shadow-lg shadow-blue-500/25' 
              : currentTheme === 'grither'
              ? 'grither-gradient-primary grither-shadow-lg hover:grither-shadow-xl'
              : 'bg-gradient-to-br from-blue-600 to-indigo-600 shadow-lg shadow-blue-500/25'
          }`}>
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          
          <h1 className={`text-3xl font-bold mb-2 ${getTextClass('primary')}`}>
            Добро пожаловать
          </h1>
          
          <p className={`text-lg ${getTextClass('secondary')}`}>
            Вход в CRM систему
          </p>
        </div>

        {error && (
          <div className={`mb-6 p-4 rounded-xl border backdrop-blur-sm ${
            currentTheme === 'dark'
              ? 'bg-red-900/20 border-red-700/50 text-red-300' 
              : currentTheme === 'grither'
              ? 'bg-red-50/90 border-red-200 text-red-700 grither-shadow'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="username" className={`flex items-center gap-2 text-sm font-semibold mb-3 ${getTextClass('secondary')}`}>
              <User size={18} strokeWidth={2} className="flex-shrink-0" /> Имя пользователя
            </label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 focus:outline-none focus:ring-4 backdrop-blur-sm ${
                currentTheme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500/20' 
                  : currentTheme === 'grither'
                  ? 'bg-white/95 border-grither-gray-300 grither-text-primary placeholder:grither-text-tertiary focus:border-grither-primary focus:ring-grither-primary/20 grither-shadow-sm focus:grither-shadow-md'
                  : currentTheme === 'custom'
                  ? 'universal-input focus:ring-4'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500/20'
              }`}
              placeholder="Введите имя пользователя"
            />
          </div>

          <div>
            <label htmlFor="password" className={`flex items-center gap-2 text-sm font-semibold mb-3 ${getTextClass('secondary')}`}>
              <Lock size={18} strokeWidth={2} className="flex-shrink-0" /> Пароль
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 focus:outline-none focus:ring-4 backdrop-blur-sm ${
                currentTheme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500/20' 
                  : currentTheme === 'grither'
                  ? 'bg-white/95 border-grither-gray-300 grither-text-primary placeholder:grither-text-tertiary focus:border-grither-primary focus:ring-grither-primary/20 grither-shadow-sm focus:grither-shadow-md'
                  : currentTheme === 'custom'
                  ? 'universal-input focus:ring-4'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500/20'
              }`}
              placeholder="Введите пароль"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${getButtonClass()} shadow-lg hover:shadow-xl`}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Вход...
              </div>
            ) : (
              'Войти в систему'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;